import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ERROR_STATE_IGNORED,
  ERROR_STATE_RESOLVED,
  errorOccurrencesQuery,
  errorsScreenQuery,
  setErrorState,
  type ErrorCode,
  type ErrorOccurrence,
} from '../../api/admErrors'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { toast } from '../../ui/notify'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import { AdmFilterBar, WindowPicker } from './AdmFilterBar'
import { AdmViews } from './AdmViews'

/**
 * ADM -> „Why discovery failed", din
 * `views/version-3/softsee/adm/errors/index.php` si din randul lui desfasurat
 * (`errors/row_expand.php`).
 *
 * O PALNIE, NU UN JURNAL
 * ----------------------
 * Categorie, apoi cod, apoi adresele din spate. Un singur segment de retea
 * nerutata inseamna mii de caderi identice, si o lista plata cu ele nu se poate
 * nici citi, nici lucra.
 *
 * Fiecare categorie raspunde altei echipe - acreditarile la cine tine contul de
 * serviciu, reteaua la cine tine firewallul - si aia e chiar ratiunea ei.
 *
 * TOT CE E DESPRE UN COD STA IN RANDUL LUI
 * ----------------------------------------
 * Ce inseamna si unde s-a intamplat erau doua file; sunt aceeasi intrebare pusa
 * de doua ori. Cine deschide o cadere vrea si propozitia (ii spune a cui e
 * problema) si adresele (ii spun cat de mare e).
 *
 * A IGNORA UN COD INTREG e singura fapta care se poate face cand un releu a
 * produs patru mii de caderi identice - forma obisnuita a unei probleme de
 * retea. De-aia butoanele stau pe rand, la nivelul codului, nu pe fiecare
 * adresa.
 *
 * Foile sunt cele ale portalului (`public/css/adm.css`).
 */

const STYLES = ['css/adm.css']

/** Cate adrese se arata in randul desfasurat - `Adm::errorTabs()` aduce 20. */
const EXPAND_ROWS = 20

/** Pastila de stare, ca in `row_expand.php`. */
const STATE_PILL: Record<number, string> = { 1: 'adm-pill-bad', 2: 'adm-pill', 3: 'adm-pill-ok' }

/** Pastila categoriei, ca in `row_expand.php`. */
const CATEGORY_PILL: Record<number, string> = {
  1: 'adm-pill-bad',
  2: 'adm-pill-warn',
  3: 'adm-pill-warn',
  4: 'adm-pill-running',
  5: 'adm-pill',
}

function count(value: number): string {
  return value.toLocaleString('en-US')
}

/*
 * `<a href>`, nu `<Link>`: la fel ca banda vederilor si ca bara modulului. Cele
 * doua marimi pe care le poarta ecranul - fereastra si categoria - stau in
 * ADRESA, deci legatura se poate da mai departe cuiva si intoarcerea din fisa
 * unui cod nimereste inapoi aceeasi felie a palniei.
 */
function listUrl(days: number, category: number): string {
  return `/app/adm/errors?days=${days}${category > 0 ? `&category=${category}` : ''}`
}

function codeUrl(code: string, days: number): string {
  return `/app/adm/errors/${encodeURIComponent(code)}?days=${days}`
}

/**
 * Randul desfasurat: ce e caderea, ce ai de facut, si unde s-a intamplat.
 *
 * Adresele se cer abia cand randul se deschide - o lista de coduri poate avea
 * douazeci de randuri, si douazeci de interogari pentru ceva ce poate nimeni nu
 * deschide ar tine pagina pe loc.
 */
function ExpandedCode({
  code,
  days,
  canWrite,
  onDecide,
}: {
  code: ErrorCode
  days: number
  canWrite: boolean
  onDecide(state: number): void
}) {
  const rows = useQuery({
    ...errorOccurrencesQuery(code.code, {
      draw: 1,
      start: 0,
      length: EXPAND_ROWS,
      orderColumn: 0,
      orderDir: 'desc',
      search: '',
      days,
      state: '',
    }),
  })

  const list: ErrorOccurrence[] = rows.data?.data ?? []
  const total = rows.data?.recordsTotal ?? 0

  return (
    <div className="adm-expand">
      <div className="row">
        <div className="col-md-5">
          <h5 className="adm-expand-head">
            <i className="fas fa-info-circle"></i> The failure
          </h5>

          <table className="table table-condensed adm-list">
            <tbody>
              <tr>
                <td style={{ width: 140 }} className="text-muted">
                  Category
                </td>
                <td>
                  <span className={`adm-pill ${CATEGORY_PILL[code.category] ?? ''}`}>{code.category_label}</span>
                </td>
              </tr>
              <tr>
                <td className="text-muted">Occurrences</td>
                <td>{count(code.occurrences)}</td>
              </tr>
              <tr>
                <td className="text-muted">Customers affected</td>
                <td>{code.companies}</td>
              </tr>
              <tr>
                <td className="text-muted">First seen</td>
                <td className="adm-nowrap">{code.first_seen}</td>
              </tr>
              <tr>
                <td className="text-muted">Last seen</td>
                <td className="adm-nowrap">{code.last_seen}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-md-7">
          <h5 className="adm-expand-head">
            <i className="fas fa-lightbulb"></i> What to do
          </h5>

          <div className="adm-guidance">{code.guidance || 'No guidance has been recorded for this code yet.'}</div>

          {canWrite ? (
            <div style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-warning btn-sm admIgnoreCode" onClick={() => onDecide(ERROR_STATE_IGNORED)}>
                <i className="fa fa-ban"></i> Ignore all
              </button>{' '}
              {/* A marca reparat e o afirmatie despre lume, nu despre inregistrare:
                  scanarea urmatoare hotaraste daca era adevarata. */}
              <button type="button" className="btn btn-success btn-sm admResolveCode" onClick={() => onDecide(ERROR_STATE_RESOLVED)}>
                <i className="fa fa-check"></i> Mark fixed
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <h5 className="adm-expand-head" style={{ marginTop: 16 }}>
        <i className="fas fa-map-marker-alt"></i> Where it happened{' '}
        {total > 0 ? (
          <span className="text-muted small">
            {total > list.length ? `first ${list.length} of ${count(total)}` : count(total)}
          </span>
        ) : null}
      </h5>

      {rows.isPending ? (
        <div className="adm-empty-inline">
          <i className="fa fa-spinner fa-spin"></i> Looking…
        </div>
      ) : rows.isError ? (
        <div className="adm-empty-inline">{(rows.error as Error).message}</div>
      ) : list.length === 0 ? (
        <div className="adm-empty-inline">
          <i className="fa fa-check-circle"></i> Nothing recorded for this code in the selected period.
        </div>
      ) : (
        <>
          <table className="table table-condensed adm-list">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Address</th>
                <th>Customer</th>
                <th>Relay</th>
                <th>Message</th>
                <th className="adm-numeric" style={{ width: 70 }}>
                  Times
                </th>
                <th style={{ width: 80 }}>State</th>
                <th style={{ width: 140 }}>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.ID}>
                  <td className="adm-nowrap">{row.target_ip}</td>
                  <td className="adm-muted">{row.company_name}</td>
                  <td className="adm-muted">{row.relay_name || '—'}</td>
                  <td>{row.message}</td>
                  <td className="adm-numeric">{count(row.occurrences)}</td>
                  <td>
                    <span className={`adm-pill ${STATE_PILL[row.state] ?? ''}`}>{row.state_label}</span>
                  </td>
                  <td className="adm-nowrap adm-muted">{row.last_seen}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {total > list.length ? (
            <p className="text-muted small" style={{ marginBottom: 0 }}>
              <a href={codeUrl(code.code, days)}>Open the code to search all {count(total)} of them</a>
              .
            </p>
          ) : null}
        </>
      )}
    </div>
  )
}

export function ErrorsPage({ days, category }: { days: number; category: number }) {
  const shellData = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shellData.data?.version ?? '')

  const [open, setOpen] = useState<string | null>(null)
  const screen = useQuery(errorsScreenQuery(days, category))

  const categories = screen.data?.categories ?? []
  const codes = screen.data?.codes ?? []
  const canWrite = screen.data?.canWrite ?? false

  const failures = categories.reduce((sum, row) => sum + row.value, 0)
  const causes = categories.reduce((sum, row) => sum + row.codes, 0)
  const tone = failures > 0 ? 'red-intense' : 'green-mode'

  const decide = async (code: string, state: number) => {
    try {
      const answer = await setErrorState({ code }, state, days)
      toast(answer.message, 'Discovery errors', 'success')
      void screen.refetch()
    } catch (error) {
      toast((error as Error).message, 'Discovery errors', 'error')
    }
  }

  const chosen = screen.data?.categoryLabels.find((option) => option.value === String(category))

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone={tone}
          icon="fa-exclamation-triangle"
          title="Why discovery failed"
          note="grouped by category, then by code, then by the addresses behind it"
          stats={[
            { value: count(failures), label: 'failures', tone },
            { value: count(causes), label: 'distinct causes', tone: 'grey' },
          ]}
        />

        <AdmViews views={screen.data?.views ?? []} current="errors" />

        <AdmFilterBar>
          <WindowPicker
            windows={screen.data?.windows ?? [1, 7, 30, 90]}
            value={days}
            onChange={(next) => {
              window.location.assign(`/app/adm/errors?days=${next}${category > 0 ? `&category=${category}` : ''}`)
            }}
          />
        </AdmFilterBar>

        {screen.isPending ? (
          <div className="adm-empty-inline">
            <i className="fa fa-spinner fa-spin"></i> Looking…
          </div>
        ) : screen.isError ? (
          <div className="adm-empty-inline">{(screen.error as Error).message}</div>
        ) : categories.length === 0 ? (
          <div className="adm-empty-inline">
            <i className="fa fa-check-circle"></i> No unresolved failures in this period.
          </div>
        ) : (
          <>
            {/* Treapta intai: a cui e problema. */}
            <div className="adm-funnel">
              {categories.map((row) => (
                <a
                  key={row.category}
                  className={`cat-${row.category}${category === row.category ? ' on' : ''}`}
                  href={listUrl(days, category === row.category ? 0 : row.category)}
                >
                  <i className={`fas ${row.icon} adm-funnel-icon`}></i>
                  <span className="adm-funnel-value">{count(row.value)}</span>
                  <span className="adm-funnel-label">
                    {row.title} · {row.codes} code(s)
                  </span>
                </a>
              ))}
            </div>

            {/* Treapta a doua: care cadere, si ce ai de facut cu ea. */}
            <div className="adm-section">
              <h4>{category > 0 ? `${chosen?.label ?? ''} failures` : 'All failures'}</h4>
              {category > 0 ? (
                <a className="adm-section-link" href={listUrl(days, 0)}>
                  Show all categories
                </a>
              ) : null}
            </div>

            <table className="table table-hover adm-list" id="admCodesTable" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 50 }}></th>
                  <th>Code</th>
                  <th>What it means</th>
                  <th className="adm-numeric">Occurrences</th>
                  <th className="adm-numeric">Customers</th>
                  <th>Last seen</th>
                  <th style={{ width: 150 }}></th>
                </tr>
              </thead>
              <tbody>
                {codes.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="adm-empty-inline">
                      Nothing in this category for the selected period.
                    </td>
                  </tr>
                ) : null}

                {codes.map((row) => (
                  <tr key={row.code} className={open === row.code ? 'opened' : undefined}>
                    <td className="text-center">
                      <span
                        className="show_details color-dark-grey pointer font-16"
                        data-id={row.code}
                        role="button"
                        tabIndex={0}
                        onClick={() => setOpen(open === row.code ? null : row.code)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') setOpen(open === row.code ? null : row.code)
                        }}
                      >
                        <i className={`fas ${open === row.code ? 'fa-minus-circle' : 'fa-plus-circle'}`}></i>
                      </span>
                    </td>

                    {open === row.code ? (
                      <td colSpan={6}>
                        <ExpandedCode code={row} days={days} canWrite={canWrite} onDecide={(state) => void decide(row.code, state)} />
                      </td>
                    ) : (
                      <>
                        <td className="adm-nowrap">
                          <a href={codeUrl(row.code, days)}>{row.code}</a>
                          <div className="adm-muted" style={{ fontSize: 11 }}>
                            {row.category_label}
                          </div>
                        </td>
                        <td className="adm-muted">{row.guidance || 'No guidance recorded for this code yet.'}</td>
                        <td className="adm-numeric">{count(row.occurrences)}</td>
                        <td className="adm-numeric">{row.companies}</td>
                        <td className="adm-nowrap adm-muted">{row.last_seen}</td>
                        <td className="text-right adm-nowrap">
                          {canWrite ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-warning btn-xs admIgnoreCode"
                                title="Stop reporting this failure"
                                onClick={() => void decide(row.code, ERROR_STATE_IGNORED)}
                              >
                                <i className="fa fa-ban"></i> Ignore
                              </button>{' '}
                              <button
                                type="button"
                                className="btn btn-success btn-xs admResolveCode"
                                title="Mark as fixed"
                                onClick={() => void decide(row.code, ERROR_STATE_RESOLVED)}
                              >
                                <i className="fa fa-check"></i>
                              </button>
                            </>
                          ) : null}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </Content>
  )
}
